<template>
  <div>
    <b-card>
      <b-row>
        <b-col md="6">
          <h1>Gestión de Periodos Académicos</h1>
        </b-col>
        <b-col md="6" class="text-right">
          <b-button variant="primary" @click="openModal(null)">
            <feather-icon icon="PlusIcon" class="mr-50" />
            Nuevo Periodo
          </b-button>
        </b-col>
      </b-row>

      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" label="Cargando..."></b-spinner>
        <p class="mt-2">Cargando períodos académicos...</p>
      </div>

      <b-table
        v-else
        striped
        hover
        responsive
        class="mt-2"
        :items="periods"
        :fields="fields"
      >
        <template #cell(estado)="data">
          <b-badge :variant="data.value ? 'success' : 'secondary'">
            {{ data.value ? 'Activo' : 'Inactivo' }}
          </b-badge>
        </template>
        <template #cell(fechaInicio)="data">
          {{ formatDate(data.value) }}
        </template>
        <template #cell(fechaFin)="data">
          {{ formatDate(data.value) }}
        </template>
        <template #cell(actions)="data">
          <b-button variant="gradient-primary" class="btn-icon" @click="openModal(data.item)">
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button variant="gradient-danger" class="btn-icon ml-1" @click="deletePeriod(data.item)">
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <period-modal
      :period-data="selectedPeriod"
      @submit="handleModalSubmit"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BSpinner, BBadge,
} from 'bootstrap-vue'
import PeriodModal from '@/components/periods/PeriodModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import periodosService from '@/services/periodosService'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BSpinner,
    BBadge,
    PeriodModal,
  },
  data() {
    return {
      fields: [
        { key: 'codigo', label: 'Código', sortable: true },
        { key: 'año', label: 'Año', sortable: true },
        { key: 'ciclo', label: 'Ciclo', sortable: true },
        { key: 'fechaInicio', label: 'Fecha Inicio', sortable: true },
        { key: 'fechaFin', label: 'Fecha Fin', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'actions', label: 'Acciones' },
      ],
      periods: [],
      selectedPeriod: null,
      loading: false,
    }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        console.log('📋 Cargando períodos desde el API...')
        const periodsResponse = await periodosService.getAll()
        this.periods = periodsResponse || []
        console.log('✅ Períodos cargados:', this.periods.length)
      } catch (error) {
        console.error('Error loading periods:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar los períodos. Intente nuevamente.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    openModal(item) {
      this.selectedPeriod = item ? { ...item } : null
      this.$bvModal.show('period-modal')
    },
    async handleModalSubmit(data) {
      try {
        console.log('📨 Datos recibidos del modal:', data)
        
        if (data.periodoId) {
          // Actualizar período existente
          console.log('🔄 Actualizando período:', data.periodoId)
          await periodosService.update(data.periodoId, data)
          const index = this.periods.findIndex(p => p.periodoId === data.periodoId)
          if (index !== -1) {
            this.periods.splice(index, 1, data)
          }
        } else {
          // Crear nuevo período
          console.log('➕ Creando nuevo período')
          const response = await periodosService.create(data)
          this.periods.push(response)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Operación Exitosa',
            icon: 'CheckIcon',
            variant: 'success',
            text: `El período se ha ${data.periodoId ? 'actualizado' : 'creado'} correctamente.`,
          },
        })
      } catch (error) {
        console.error('Error saving period:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al guardar el período. Intente nuevamente.',
          },
        })
      }
    },
    deletePeriod(item) {
      this.$swal({
        title: '¿Estás seguro?',
        text: '¡No podrás revertir esto!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, ¡elimínalo!',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          try {
            console.log('🗑️ Eliminando período:', item.periodoId)
            await periodosService.delete(item.periodoId)
            const index = this.periods.findIndex(p => p.periodoId === item.periodoId)
            if (index !== -1) {
              this.periods.splice(index, 1)
            }
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Eliminado',
                icon: 'TrashIcon',
                variant: 'success',
                text: 'El período ha sido eliminado.',
              },
            })
          } catch (error) {
            console.error('Error deleting period:', error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: 'Error al eliminar el período. Intente nuevamente.',
              },
            })
          }
        }
      })
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('es-GT')
    },
  },
}
</script>
